import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Landmark, Check, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";

const roles = {
  realEstate: {
    title: "Real Estate Agent",
    icon: Home,
    color: "red",
    responsibilities: [
      "Run personalized home searches for pre-approved buyers",
      "Guide buyers through the entire homebuying process",
      "Write and negotiate offers on behalf of clients",
      "Coordinate with showing assistants for property tours",
      "Move clients from initial search to closing",
    ],
    benefits: [
      "Pre-approved leads delivered directly to you",
      "No prospecting or cold calling required",
      "Showing assistants handle in-person tours",
      "Transaction coordinators manage paperwork",
      "Full technology stack provided",
    ],
  },
  mortgage: {
    title: "Mortgage Banker",
    icon: Landmark,
    color: "blue",
    responsibilities: [
      "Structure loan options for pre-approved buyers",
      "Review and process mortgage applications",
      "Guide clients through financing decisions",
      "Coordinate with processors and underwriters",
      "Move loans from pre-approval to closing",
    ],
    benefits: [
      "Pre-qualified leads ready for financing",
      "In-house processors and underwriters",
      "Integrated technology platform",
      "Direct collaboration with real estate team",
      "Faster close rates with unified workflow",
    ],
  },
};

const RoleOverviewSection = () => {
  const [activeRole, setActiveRole] = useState("realEstate");
  const role = roles[activeRole];
  const Icon = role.icon;

  const scrollToApply = () => {
    document.getElementById("apply-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="role-overview" className="py-24 md:py-32 relative bg-white" data-testid="role-overview-section">
      <div className="container-main relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-sm font-semibold tracking-widest uppercase text-blue-600 mb-4 block">
            Your Role
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-slate-900">
            Two Paths,{" "}
            <span className="gradient-text">One Mission</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Whether you're in real estate or mortgage, CityWorth provides the infrastructure
            for you to focus on what matters: helping clients achieve homeownership.
          </p>
        </motion.div>

        {/* Role Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex justify-center mb-12"
        >
          <div className="role-toggle">
            <div
              className="role-toggle-indicator"
              style={{
                left: activeRole === "realEstate" ? "4px" : "50%",
                width: "calc(50% - 4px)",
              }}
            />
            <button
              onClick={() => setActiveRole("realEstate")}
              className={`role-toggle-option ${activeRole === "realEstate" ? "active" : ""}`}
              data-testid="toggle-real-estate"
            >
              <Home className="w-4 h-4 inline mr-2" />
              Real Estate
            </button>
            <button
              onClick={() => setActiveRole("mortgage")}
              className={`role-toggle-option ${activeRole === "mortgage" ? "active" : ""}`}
              data-testid="toggle-mortgage"
            >
              <Landmark className="w-4 h-4 inline mr-2" />
              Mortgage
            </button>
          </div>
        </motion.div>

        {/* Role Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeRole}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="grid md:grid-cols-2 gap-8"
          >
            {/* Responsibilities */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  activeRole === "realEstate" 
                    ? "bg-gradient-to-br from-red-500 to-red-600" 
                    : "bg-gradient-to-br from-blue-500 to-blue-600"
                }`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">{role.title}</h3>
                  <p className="text-slate-500">Key Responsibilities</p>
                </div>
              </div>
              
              <ul className="space-y-4">
                {role.responsibilities.map((item, index) => (
                  <motion.li
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0 ${
                      activeRole === "realEstate" ? "bg-red-100" : "bg-blue-100"
                    }`}>
                      <Check className={`w-3 h-3 ${
                        activeRole === "realEstate" ? "text-red-500" : "text-blue-500"
                      }`} />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Benefits */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">What You Get</h3>
                <p className="text-slate-500">CityWorth provides everything you need</p>
              </div>
              
              <ul className="space-y-4 mb-8">
                {role.benefits.map((item, index) => (
                  <motion.li
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 + 0.2 }}
                    className="flex items-start gap-3"
                  >
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <Check className="w-3 h-3 text-green-500" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </motion.li>
                ))}
              </ul>

              <Button
                onClick={scrollToApply}
                className="w-full btn-primary py-6 rounded-xl font-semibold text-white"
                data-testid="role-apply-btn"
              >
                Apply as {role.title}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Bottom note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center text-slate-500 mt-8"
        >
          Each professional works with multiple active buyers at different stages of the journey.
        </motion.p>
      </div>
    </section>
  );
};

export default RoleOverviewSection;
